import React from 'react';
import { ArtifactType, Solicitacao } from '@/models/Solicitacao';
import { usePrescricaoLivreContext } from '@/features/prescricao-livre/providers/PrescricaoLivreProvider';
import {
  defaultItemStatus,
  itemStateToApi,
} from '@/features/prescricao-livre/helpers/parsers';
import { useExecucaoContext } from '../ExecucaoProvider';

interface UseSolicitacoes {
  solicitacoes: Solicitacao[];
  solicitacoesPorTipo: (tipoArtefato: ArtifactType) => Solicitacao[];
}

export function useSolicitacoes(): UseSolicitacoes {
  const { items: prescricaoLivre } = usePrescricaoLivreContext();
  const [{ protocoloExecutado, passosExecutados }] = useExecucaoContext();

  const solicitacoes = React.useMemo(() => {
    const items: Solicitacao[] = [];

    if (passosExecutados) {
      items.push(
        ...passosExecutados
          .map(passo => (passo.solicitacoes as unknown) as Solicitacao[])
          .flat()
          .filter(solicitacao => solicitacao.data_solicitacao !== null),
      );
    }

    if (protocoloExecutado) {
      items.push(
        ...prescricaoLivre
          .filter(
            ({
              itemState: {
                isAdding = false,
                isCanceling = false,
                isEditing = false,
                isSubmitting = false,
              } = defaultItemStatus,
            }) => !(isAdding || isCanceling || isEditing || isSubmitting),
          )
          .map<Solicitacao>(item => itemStateToApi(item)),
      );
    }

    return items;
  }, [protocoloExecutado, passosExecutados, prescricaoLivre]);

  const solicitacoesPorTipo = React.useCallback(
    (tipoArtefato: ArtifactType) => {
      return solicitacoes.filter(
        solicitacao => solicitacao.tipo === tipoArtefato,
      );
    },
    [solicitacoes],
  );

  return {
    solicitacoes,
    solicitacoesPorTipo,
  };
}
